import { AfterViewInit, Component, DestroyRef, ElementRef, ViewChild, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { COURSE_MODEL_TYPE, LiveLesson } from 'src/app/types/live-lesson-types';
import { LiveLessonTagComponent } from '../../live-lesson-tag/live-lesson-tag.component';
import { FeatureStore } from 'src/app/stores/feature-store';
import { SubscriptionStore } from 'src/app/stores/subscription-store';

@Component({
  selector: 'app-live-lesson-schedule-item',
  standalone: true,
  imports: [CommonModule, RouterLink, LiveLessonTagComponent],
  templateUrl: './live-lesson-schedule-item.component.html',
  styleUrl: './live-lesson-schedule-item.component.scss',
})
export class LiveLessonScheduleItemComponent implements AfterViewInit {
  lesson = input.required<LiveLesson>();

  @ViewChild('descriptionEl') descriptionEl?: ElementRef<HTMLElement>;
  private destroyRef = inject(DestroyRef);
  private featureStore = inject(FeatureStore);
  private subscriptionStore = inject(SubscriptionStore);

  showFullDescription = signal(false);
  /** Whether the 3-line-clamped description is actually clipping text — see ngAfterViewInit(). */
  isDescriptionTruncated = signal(false);

  readonly isLive = computed(() => {
    const now = new Date();
    return new Date(this.lesson().starts_at) <= now && new Date(this.lesson().ends_at!) >= now;
  });

  readonly courseTag = computed(() =>
    this.lesson().tags.find((t) => t.model_type === COURSE_MODEL_TYPE) ?? null,
  );

  readonly topicTags = computed(() => {
    const course = this.courseTag();
    return this.lesson().tags.filter((t) => t.id !== course?.id);
  });

  // The whole card is now a routerLink <a> (see template) — without stopping propagation here,
  // toggling the description would also navigate away, same reasoning as
  // LiveLessonTagComponent.onActivate().
  toggleDescription(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.showFullDescription.set(!this.showFullDescription());
  }

  readonly tierBadge = computed(() => {
    const tier = this.lesson().access_tier;
    const hasPremium = this.featureStore.hasFeature('live-lessons-feature');
    const isSubscribed = this.subscriptionStore.isSubscribed();

    if (tier === 'premium' && !hasPremium) return 'premium';
    if (tier === 'standard' && !isSubscribed && !hasPremium) return 'standard';
    return null;
  });

  ngAfterViewInit(): void {
    const el = this.descriptionEl?.nativeElement;
    if (!el) return;

    // A ResizeObserver rather than a one-shot check — the clamped height can still change after
    // mount (e.g. the Poppins webfont finishing its swap), which would otherwise leave a stale
    // "nothing more to show" verdict taken from a fallback-font measurement. Skipped once
    // expanded: .expanded removes the clamp entirely, so scrollHeight/clientHeight naturally
    // converge and would otherwise flip this back to false mid-read, hiding "Show Less".
    const observer = new ResizeObserver(() => {
      if (this.showFullDescription()) return;
      this.isDescriptionTruncated.set(el.scrollHeight > el.clientHeight + 1);
    });
    observer.observe(el);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }
}
